/**
 * Models documentation page
 */

import {DocsContent} from '../../components/docs/DocsContent';
import {DocsTable} from '../../components/docs/DocsTable';
import {DocsActionButtons} from '../../components/docs/DocsActionButtons';

export function Models() {
  return (
    <DocsContent
      title="AI Models"
      description="VoxaPilot uses advanced AI models for transcription, summarization, and analysis."
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Available Models</h2>
          <p className="text-textSecondary mb-6">
            VoxaPilot supports multiple AI models for different tasks. Each model is optimized for
            specific use cases.
          </p>

          <DocsTable
            headers={['Model', 'Max Context', 'Capabilities']}
            rows={[
              [
                'Whisper Large v3',
                '200k tokens',
                'Transcription, Multi-language support, High accuracy',
              ],
              [
                'GPT-4 Turbo',
                '128k tokens',
                'Summarization, Action items, Sentiment analysis',
              ],
              [
                'Claude 3.5 Sonnet',
                '200k tokens',
                'Summarization, Key insights, Topic extraction',
              ],
              [
                'Gemini Pro',
                '32k tokens',
                'Quick summaries, Real-time processing',
              ],
            ]}
          />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Transcription Models</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot primarily uses OpenAI's Whisper models for transcription. These models
            provide:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>High accuracy (99%+ for clear audio)</li>
            <li>Multi-language support (100+ languages)</li>
            <li>Real-time and batch processing</li>
            <li>Speaker diarization</li>
            <li>Punctuation and formatting</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Summarization Models</h2>
          <p className="text-textSecondary mb-4">
            For generating summaries and insights, VoxaPilot uses large language models:
          </p>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">GPT-4 Turbo</h3>
          <p className="text-textSecondary mb-4">
            Best for comprehensive summaries, action item extraction, and detailed analysis. Ideal
            for longer meetings and complex discussions.
          </p>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Claude 3.5 Sonnet</h3>
          <p className="text-textSecondary mb-4">
            Excellent for structured summaries and key insight extraction. Great balance of speed
            and quality.
          </p>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Gemini Pro</h3>
          <p className="text-textSecondary mb-4">
            Fast and efficient for quick summaries. Best for shorter meetings and real-time
            processing.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Model Selection</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot automatically selects the best model based on:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Meeting length and complexity</li>
            <li>Your plan tier</li>
            <li>Processing speed requirements</li>
            <li>Available model capacity</li>
          </ul>

          <p className="text-textSecondary">
            Enterprise customers can configure model preferences and set custom rules for model
            selection.
          </p>
        </section>

        <DocsActionButtons pageTitle="AI Models" />
      </div>
    </DocsContent>
  );
}

